import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import {
  Card,
  Button,
  message,
  RangePicker,
  Table,
  TableProps,
} from '@bb/elements-web';
import type { KeyholderInvoiceData } from '@/app-package';
import { formatCurrency } from 'utils';
import { getKeyholderInvoices } from 'api/SupraController';
import sendSupraBillingHistoryEmail from 'api/SupraController/sendSupraBillingHistoryEmail';
import { useSubscriptionsPageContext } from '../../SubscriptionsPageContext';
import { Invoice, InvoiceStatus, InvoiceFilters } from './types';
import StatusFilterDropdown from './components/StatusFilterDropdown';

// ============================================================================
// Constants
// ============================================================================

const COLUMN_WIDTH = 204;
const AMOUNT_MIN_WIDTH = 60;
const DATE_PICKER_WIDTH = 320;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Maps raw API response to our Invoice type.
 * Transforms date strings to Date objects and normalizes status values.
 */
function mapInvoiceResponse(response: KeyholderInvoiceData): Invoice {
  return {
    id: String(response.billingPlatformInvoiceId),
    invoiceNumber: response.invoiceNumber,
    createdAt: new Date(response.createdAt),
    amount: response.amount,
    status:
      response.status === 'PAID' ? InvoiceStatus.Paid : InvoiceStatus.Unpaid,
  };
}

/**
 * Filters invoices based on status and date range.
 * All filters are optional and applied cumulatively.
 */
function filterInvoices(
  invoices: Invoice[],
  filters: InvoiceFilters,
): Invoice[] {
  let filtered = invoices;

  if (filters.status) {
    filtered = filtered.filter(invoice => invoice.status === filters.status);
  }

  if (filters.createdAfter) {
    filtered = filtered.filter(
      invoice => new Date(invoice.createdAt) >= filters.createdAfter!,
    );
  }

  if (filters.createdBefore) {
    filtered = filtered.filter(
      invoice => new Date(invoice.createdAt) <= filters.createdBefore!,
    );
  }

  return filtered;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Displays a list of keyholder billing invoices with filtering and email capabilities.
 *
 * Features:
 * - Fetches invoices from the billing API
 * - Filter by payment status (Paid/Unpaid)
 * - Filter by date range
 * - Email invoice history to user
 *
 * Requires SubscriptionsPageProvider to be present in the component tree.
 */
const InvoicesCard: React.FC = () => {
  const { billingProfile, loadingBillingProfile } =
    useSubscriptionsPageContext();

  // Check if billing profile exists
  const hasBillingProfile =
    !!billingProfile?.BoardBillingNumber && !!billingProfile?.UserBillingNumber;

  // Data state
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [sendingBillingHistoryEmail, setSendingBillingHistoryEmail] =
    useState(false);

  // Payment status filter state
  const [filters, setFilters] = useState<InvoiceFilters>({
    status: undefined,
    createdAfter: undefined,
    createdBefore: undefined,
  });

  // Table pagination state (how many rows to show at a time)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Memoize filtered data source to avoid recalculating on every render
  const dataSource = useMemo(
    () => filterInvoices(allInvoices, filters),
    [allInvoices, filters],
  );

  // Memoize columns definition since it doesn't depend on component state
  const columns: TableProps<Invoice>['columns'] = useMemo(
    () => [
      {
        key: 'createdAt',
        dataIndex: 'createdAt',
        title: 'Date',
        width: COLUMN_WIDTH,
        align: 'left',
        render: (createdAt: Date) => dayjs(createdAt).format('MMM D, YYYY'),
      },
      {
        key: 'invoiceNumber',
        dataIndex: 'invoiceNumber',
        title: 'Invoice Number',
        width: COLUMN_WIDTH,
        align: 'left',
        render: (invoiceNumber: string) => `Invoice #${invoiceNumber}`,
      },
      {
        key: 'amount',
        dataIndex: 'amount',
        title: 'Amount',
        width: COLUMN_WIDTH,
        align: 'left',
        filterDropdown: StatusFilterDropdown,
        render: (amount: number, record: Invoice) => (
          <AmountCell>
            <span>{amount ? formatCurrency(amount) : '$0.00'}</span>
            <StatusPill $status={record.status}>
              {record.status === InvoiceStatus.Paid ? 'Paid' : 'Unpaid'}
            </StatusPill>
          </AmountCell>
        ),
      },
    ],
    [],
  );

  // Fetch invoices when billingProfile becomes available
  useEffect(() => {
    if (
      !billingProfile?.BoardBillingNumber ||
      !billingProfile?.UserBillingNumber
    ) {
      return;
    }

    let isMounted = true;

    const loadInvoices = async () => {
      setLoading(true);

      try {
        const response = await getKeyholderInvoices({
          supraOCN: billingProfile.BoardBillingNumber,
          billingCustomerNumber: billingProfile.UserBillingNumber,
        });

        if (!isMounted) return;

        const mappedInvoices = response.map(mapInvoiceResponse);
        setAllInvoices(mappedInvoices);
      } catch (error) {
        if (isMounted) {
          setAllInvoices([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadInvoices();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [billingProfile?.BoardBillingNumber, billingProfile?.UserBillingNumber]);

  /**
   * Handles date range picker changes.
   * Converts selected dates to start/end of day for accurate filtering.
   */
  const handleDateChange: NonNullable<
    React.ComponentProps<typeof RangePicker>['onChange']
  > = (_, dateStrings) => {
    const createdAfter = dateStrings?.[0]
      ? dayjs(dateStrings[0]).startOf('day').toDate()
      : undefined;
    const createdBefore = dateStrings?.[1]
      ? dayjs(dateStrings[1]).endOf('day').toDate()
      : undefined;

    // Now that it's formatted, save the selected dates as the filter.
    setFilters(prev => ({
      ...prev,
      createdAfter,
      createdBefore,
    }));
  };

  // Any time any of the filters changes, reset to the first page of results.
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.status, filters.createdAfter, filters.createdBefore]);

  /**
   * Sends billing history email to the user.
   * Uses current date filters if set.
   */
  const handleSendEmail = async () => {
    if (
      !billingProfile?.BoardBillingNumber ||
      !billingProfile?.UserBillingNumber
    ) {
      message.error('Unable to send email. Missing billing information.');
      return;
    }

    try {
      // Format dates for API (ISO 8601 format)
      const startDate = filters.createdAfter
        ? `${dayjs(filters.createdAfter).format().slice(0, 19)}Z`
        : undefined;
      const endDate = filters.createdBefore
        ? `${dayjs(filters.createdBefore).format().slice(0, 19)}Z`
        : undefined;

      setSendingBillingHistoryEmail(true);

      await sendSupraBillingHistoryEmail({
        supraOCN: billingProfile.BoardBillingNumber,
        billingCustomerNumber: billingProfile.UserBillingNumber,
        startDate,
        endDate,
      });

      message.success('Email sent. Check your inbox.');
    } catch (e) {
      message.error(
        'Something went wrong. If this issue persists, please contact support.',
      );
    } finally {
      setSendingBillingHistoryEmail(false);
    }
  };

  /**
   * Handles re-rendering the table, either because the user selected a new status
   * from the status filter dropdown, because they selected a new amount of rows
   * to show per page, or because they selected a new page to display.
   */
  const handleTableChange: NonNullable<TableProps<Invoice>['onChange']> = (
    pagination,
    filtersValues,
  ) => {
    // Show the correct amount of results from the correct page.
    setCurrentPage(pagination?.current || 1);
    setPageSize(pagination?.pageSize || 10);

    // Show results that match the selected payment status filter.
    setFilters(prev => ({
      ...prev,
      status:
        filtersValues.amount?.length === 1
          ? (filtersValues.amount[0] as InvoiceStatus)
          : undefined,
    }));
  };

  const isLoading = loadingBillingProfile || loading;

  // Return the actual JSX to be displayed.
  return (
    <Card
      title="Invoices"
      extra={
        <StyledExtra>
          <Button
            dataCy="email-invoices-btn"
            type="secondary"
            size="small"
            loading={sendingBillingHistoryEmail}
            disabled={!hasBillingProfile || sendingBillingHistoryEmail}
            onClick={handleSendEmail}
            minWidth="auto"
          >
            Email Invoices
          </Button>
          <RangePicker
            dataCy="invoices-date-range-picker"
            onChange={handleDateChange}
            allowClear
            disabled={!hasBillingProfile}
            style={{ width: DATE_PICKER_WIDTH }}
          />
        </StyledExtra>
      }
      dataCy="invoices-card"
    >
      <Table
        dataCy="invoices-table"
        dataSource={dataSource}
        rowKey="id"
        columns={columns}
        pagination={{
          current: currentPage,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} invoices`,
        }}
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        onChange={handleTableChange}
        locale={{
          emptyText: 'No invoices found',
        }}
      />
    </Card>
  );
};

export default InvoicesCard;

// Re-export types for consumers
export { InvoiceStatus } from './types';
export type { Invoice, InvoiceFilters } from './types';

// ============================================================================
// Styled Components
// ============================================================================

const StyledExtra = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 10px;
  padding: 12px 0px;
  align-items: center;
`;

const AmountCell = styled.div`
  display: inline-flex;
  align-items: center;

  > span:first-child {
    min-width: ${AMOUNT_MIN_WIDTH}px;
    text-align: left;
    margin-right: 8px;
  }
`;

const StatusPill = styled.span<{ $status: InvoiceStatus }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid
    ${({ $status }) => ($status === InvoiceStatus.Paid ? '#a8ceff' : '#ff4d4f')};
  background-color: ${({ $status }) =>
    $status === InvoiceStatus.Paid ? '#f0f7ff' : '#ffccc7'};
  color: ${({ $status }) =>
    $status === InvoiceStatus.Paid ? '#1b4298' : '#ffffff'};
`;
